import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable, of } from 'rxjs';
import { map, take, switchMap, catchError } from 'rxjs/operators';
import { LoginService } from '../services/api';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationGuard implements CanActivate {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private loginService: LoginService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const isOktaCallback = state.url.includes('?code=') || state.url.includes('&code=');

    if (isOktaCallback) {
      return of(true);
    }

    const existingToken = this.oidcSecurityService.getAccessToken('OKTA') || this.authenticationService.currentTokenValue;
    if (existingToken) {
      const tokenCheck = this.authenticationService.checkToken(existingToken);
      if (tokenCheck && tokenCheck.valid) {
        const provider = this.oidcSecurityService.getAccessToken('OKTA') ? 'OKTA' : 'AZURE';
        return this.processRoles(existingToken, provider);
      }
    }

    return this.oidcSecurityService.isAuthenticated$.pipe(
      take(1),
      switchMap((result) => {
        const fallbackToken = this.oidcSecurityService.getAccessToken('OKTA') || this.authenticationService.currentTokenValue;
        if (!result?.isAuthenticated && !fallbackToken) {
          return of(this.router.parseUrl('/login'));
        }
        const tokenCheck = this.authenticationService.checkToken(fallbackToken);
        if (!tokenCheck || !tokenCheck.valid) {
          return of(this.router.parseUrl('/login'));
        }
        const provider = this.oidcSecurityService.getAccessToken('OKTA') ? 'OKTA' : 'AZURE';
        return this.processRoles(fallbackToken, provider);
      }),
      catchError(() => {
        const fallbackToken = this.oidcSecurityService.getAccessToken('OKTA') || this.authenticationService.currentTokenValue;
        if (fallbackToken) {
          const tokenCheck = this.authenticationService.checkToken(fallbackToken);
          if (tokenCheck && tokenCheck.valid) {
            const provider = this.oidcSecurityService.getAccessToken('OKTA') ? 'OKTA' : 'AZURE';
            return this.processRoles(fallbackToken, provider);
          }
        }
        return of(this.router.parseUrl('/login'));
      })
    );
  }

  private processRoles(token: string, provider: 'OKTA' | 'AZURE'): Observable<boolean | UrlTree> {
    if (token) {
      sessionStorage.setItem(
        `${provider}_ROLES_CACHE`,
        JSON.stringify({
          authnResult: { access_token: token }
        })
      );
    }
    const rolesData = sessionStorage.getItem(this.authenticationService.ROLES);
    if (rolesData) {
      try {
        const roleStorage = JSON.parse(rolesData);
        if (roleStorage?.userData && roleStorage.userData.length > 0) {
          this.authenticationService.authType = provider;
        }
      } catch (e) {
        console.error(e);
      }
    }

    return this.loginService.rolUserGet().pipe(
      map((roles: any) => {

        if (!Array.isArray(roles) || roles.length === 0) {
          const errorKey = roles?.err?.key || 'ERROR.USER.NOT.AUTHORIZED';
  
          sessionStorage.setItem('UNAUTHORIZED_ERROR_KEY', errorKey);
          return this.router.createUrlTree(['/unauthorized']);
        }
        this.authenticationService.setUserRole(roles);
        this.authenticationService.authType = provider;
        return true;
      }),
      catchError((error) => {
        console.error(error);
        sessionStorage.setItem('UNAUTHORIZED_ERROR_KEY', 'ERROR.USER.NOT.AUTHORIZED');
        return of(this.router.createUrlTree(['/unauthorized']));
      })
    );
  }
}
